import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import { bakeMarkupIntoItems } from "@/lib/markup";
import { useAuth } from "@/context/AuthContext";
import { useGst } from "@/context/GstContext";
import { useLang } from "@/context/LangContext";
import { Plus, Trash2, Save, FileText, Info, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import HireUsCTA from "@/components/HireUsCTA";
import EmailResultsCard from "@/components/EmailResultsCard";

const GST = 0.15;

function computeTotals({ labour_hours, hourly_rate, items, markup_percent, gst_inclusive, gst_registered = true }) {
  const labour = Number(labour_hours || 0) * Number(hourly_rate || 0);
  const itemsTotal = items.reduce(
    (s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0),
    0,
  );
  const pre = labour + itemsTotal;
  const markup = (pre * Number(markup_percent || 0)) / 100;
  const base = pre + markup;
  const rate = gst_registered ? GST : 0;
  if (gst_inclusive) {
    const total = base;
    const ex = rate > 0 ? total / (1 + rate) : total;
    return { labour, itemsTotal, markup, subtotal: ex, gst: total - ex, total };
  }
  const gst = base * rate;
  return { labour, itemsTotal, markup, subtotal: base, gst, total: base + gst };
}

export default function QuoteCalc() {
  const { user } = useAuth();
  const { gstRegistered } = useGst();
  const { t } = useLang();
  const [title, setTitle] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [labourHours, setLabourHours] = useState(8);
  const [hourlyRate, setHourlyRate] = useState(85);
  const [items, setItems] = useState([
    { description: "Materials", quantity: 1, unit_price: 250, kind: "material" },
  ]);
  const [markup, setMarkup] = useState(15);
  const [gstInclusive, setGstInclusive] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api
      .get("/settings")
      .then((r) => {
        if (r.data?.default_hourly_rate) setHourlyRate(r.data.default_hourly_rate);
        if (r.data?.default_markup_percent !== undefined)
          setMarkup(r.data.default_markup_percent);
      })
      .catch(() => {});
  }, [user]);

  const totals = useMemo(
    () =>
      computeTotals({
        labour_hours: labourHours,
        hourly_rate: hourlyRate,
        items,
        markup_percent: markup,
        gst_inclusive: gstInclusive,
        gst_registered: gstRegistered,
      }),
    [labourHours, hourlyRate, items, markup, gstInclusive, gstRegistered],
  );

  // Client-view toggle — when ON, hide the Markup line and show labour/materials
  // with markup invisibly baked in (matches exactly what the client will see).
  const [clientView, setClientView] = useState(false);
  const markupFactor = 1 + (Number(markup) || 0) / 100;
  const labourClientView = totals.labour * markupFactor;
  const materialsClientView = totals.itemsTotal * markupFactor;

  const setItem = (idx, key, val) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, [key]: val } : it)));
  };

  const addItem = () =>
    setItems((arr) => [...arr, { description: "", quantity: 1, unit_price: 0, kind: "material" }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));

  const saveQuote = async () => {
    if (!user) {
      toast.error("Sign in to save quotes");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title || "Untitled quote",
        client_id: clientId || null,
        client_name: clients.find((c) => c.id === clientId)?.name || null,
        labour_hours: Number(labourHours) || 0,
        hourly_rate: Number(hourlyRate) || 0,
        items: items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          kind: i.kind,
        })),
        markup_percent: Number(markup) || 0,
        gst_inclusive: gstInclusive,
      };
      await api.post("/jobs", payload);
      toast.success("Quote saved");
    } catch (e) {
      toast.error("Could not save quote");
    } finally {
      setSaving(false);
    }
  };

  const createInvoice = async () => {
    if (!user) {
      toast.error("Sign in to create invoices");
      return;
    }
    try {
      const allItems = [
        ...(Number(labourHours) > 0
          ? [
              {
                description: "Labour",
                quantity: Number(labourHours),
                unit_price: Number(hourlyRate),
                kind: "labour",
              },
            ]
          : []),
        ...items.map((i) => ({
          description: i.description,
          quantity: Number(i.quantity) || 0,
          unit_price: Number(i.unit_price) || 0,
          kind: i.kind,
        })),
      ];
      // Bake markup INVISIBLY into each line's unit price. The customer-facing
      // invoice should never show a "Markup X%" line — that's an internal
      // pricing input only the tradie should see.
      const allItemsWithMarkup = bakeMarkupIntoItems(allItems, markup);
      const payload = {
        client_id: clientId || null,
        client_name: clients.find((c) => c.id === clientId)?.name || null,
        items: allItemsWithMarkup,
        gst_inclusive: gstInclusive,
      };
      const { data } = await api.post("/invoices", payload);
      toast.success(`Invoice ${data.invoice_number} created`);
      window.location.href = `/app/invoices/${data.id}`;
    } catch {
      toast.error("Could not create invoice");
    }
  };

  return (
    <div className="stagger-in">
      <div className="label-mono">/ {t("common.calculator", "CALCULATOR")} · 02</div>
      <h1 className="font-display text-5xl tracking-tight mt-2">{t("quote.h1", "Quote Builder")}</h1>
      <p className="text-zinc-400 mt-2">
        {t("quote.sub", "Labour + materials + markup + GST. Save it or turn it into an invoice.")}
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 mt-10 border border-[var(--border)]">
        {/* LEFT: inputs */}
        <div className="lg:col-span-2 p-4 sm:p-6 md:p-8 border-r border-[var(--border)] space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="label-mono mb-2">Quote title</div>
              <input
                className="input-sharp"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Kitchen fitout — Smith"
                data-testid="quote-title-input"
              />
            </div>
            <div>
              <div className="label-mono mb-2">Client</div>
              <select
                className="input-sharp"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                data-testid="quote-client-select"
              >
                <option value="">— None —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <div className="label-mono mb-3">Labour</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabeledNumber
                label="Hours"
                value={labourHours}
                onChange={setLabourHours}
                testid="quote-hours-input"
              />
              <LabeledNumber
                label="Rate / hr"
                value={hourlyRate}
                onChange={setHourlyRate}
                testid="quote-rate-input"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">Line items</div>
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={addItem}
                data-testid="quote-add-item"
              >
                <Plus size={12} /> Add
              </button>
            </div>
            <div className="border border-[var(--border)] overflow-x-auto">
              <div className="min-w-[560px]">
              <div className="grid grid-cols-12 gap-0 text-xs font-mono uppercase tracking-wider text-zinc-500 border-b border-[var(--border)]">
                <div className="col-span-6 p-3">Description</div>
                <div className="col-span-2 p-3 border-l border-[var(--border)]">Qty</div>
                <div className="col-span-3 p-3 border-l border-[var(--border)]">Unit $</div>
                <div className="col-span-1 p-3 border-l border-[var(--border)]" />
              </div>
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-0 border-b border-[var(--border)] last:border-b-0"
                  data-testid={`quote-item-${idx}`}
                >
                  <input
                    className="col-span-6 bg-transparent p-3 font-mono text-sm focus:outline-none focus:bg-[var(--surface)]"
                    value={it.description}
                    onChange={(e) => setItem(idx, "description", e.target.value)}
                    placeholder="Description"
                  />
                  <input
                    type="number"
                    step="0.01"
                    className="col-span-2 bg-transparent p-3 font-mono text-sm border-l border-[var(--border)] focus:outline-none focus:bg-[var(--surface)]"
                    value={it.quantity}
                    onChange={(e) => setItem(idx, "quantity", e.target.value)}
                  />
                  <input
                    type="number"
                    step="0.01"
                    className="col-span-3 bg-transparent p-3 font-mono text-sm border-l border-[var(--border)] focus:outline-none focus:bg-[var(--surface)]"
                    value={it.unit_price}
                    onChange={(e) => setItem(idx, "unit_price", e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    className="col-span-1 border-l border-[var(--border)] text-zinc-500 hover:text-[var(--error)] flex items-center justify-center"
                    data-testid={`quote-remove-item-${idx}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="label-mono">Markup %</div>
                <Link
                  to="/app/calc/margin"
                  className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-[var(--primary)]"
                  data-testid="quote-markup-explainer-link"
                >
                  <Info size={10} /> markup vs margin?
                </Link>
              </div>
              <input
                type="number"
                step="0.01"
                className="input-sharp"
                value={markup}
                onChange={(e) => setMarkup(e.target.value)}
                data-testid="quote-markup-input"
              />
              <div className="text-[10px] font-mono text-zinc-500 mt-2 leading-snug">
                Markup is added <span className="text-zinc-300">on top of</span>{" "}
                cost. A 25% markup gives a 20% margin —{" "}
                <Link to="/app/calc/margin" className="text-[var(--primary)] underline">
                  see the maths
                </Link>
                .
              </div>
            </div>
            <label className="flex items-center gap-3 mt-6 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={gstInclusive}
                onChange={(e) => setGstInclusive(e.target.checked)}
                className="w-4 h-4 accent-[var(--primary)]"
                data-testid="quote-gst-inclusive"
              />
              <span className="font-mono text-sm uppercase tracking-wider text-zinc-400">
                Amounts include GST
              </span>
            </label>
          </div>
        </div>

        {/* RIGHT: totals */}
        <div className="p-4 sm:p-6 md:p-8 bg-[var(--surface)]">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="label-mono">
                / {clientView ? "CLIENT VIEW" : "TICKET"}
              </div>
              <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mt-1">
                {clientView
                  ? "Exactly what your client sees · no markup line"
                  : "Your working view · markup is hidden on the invoice you send"}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setClientView((s) => !s)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 border font-mono text-[10px] uppercase tracking-wider transition-colors shrink-0 ${
                clientView
                  ? "bg-[var(--primary)] text-[var(--on-primary)] border-[var(--primary)]"
                  : "border-[var(--border)] text-zinc-400 hover:border-[var(--primary)]"
              }`}
              title={
                clientView
                  ? "Switch back to your working view"
                  : "Preview exactly what the client will see"
              }
              data-testid="quote-client-view-toggle"
              aria-pressed={clientView}
            >
              {clientView ? <Eye size={12} /> : <EyeOff size={12} />}
              {clientView ? "Client view" : "Preview client view"}
            </button>
          </div>
          <div className="mt-4 space-y-3 font-mono text-sm">
            <Row
              label="Labour"
              value={fmtMoney(clientView ? labourClientView : totals.labour)}
            />
            <Row
              label="Materials"
              value={fmtMoney(clientView ? materialsClientView : totals.itemsTotal)}
            />
            {!clientView && (
              <Row
                label={`Markup ${markup}% · internal`}
                value={fmtMoney(totals.markup)}
              />
            )}
            <div className="border-t border-[var(--border)] pt-3">
              <Row label="Subtotal" value={fmtMoney(totals.subtotal)} testid="quote-subtotal" />
              <Row
                label="GST 15%"
                value={fmtMoney(totals.gst)}
                testid="quote-gst"
                accent
              />
            </div>
            <div className="border-t border-[var(--border)] pt-3">
              <Row
                label="Total"
                value={fmtMoney(totals.total)}
                testid="quote-total"
                big
              />
            </div>
          </div>

          <div className="mt-8 space-y-3">
            <button
              onClick={saveQuote}
              disabled={saving || !user}
              className="btn-primary w-full justify-center"
              data-testid="quote-save-btn"
            >
              <Save size={14} /> {saving ? "Saving…" : "Save quote"}
            </button>
            <button
              onClick={createInvoice}
              disabled={!user}
              className="btn-ghost w-full justify-center"
              data-testid="quote-to-invoice-btn"
            >
              <FileText size={14} /> Create invoice
            </button>
            {!user && (
              <div className="text-xs font-mono text-zinc-500 text-center">
                Sign in to save or invoice
              </div>
            )}
          </div>
          <div className="hi-vis-stripe h-1.5 mt-8 -mx-4 sm:-mx-6 md:-mx-8 -mb-4 sm:-mb-6 md:-mb-8" />
        </div>
      </div>

      <EmailResultsCard
        calculator="quote"
        payload={{
          title: title || "Untitled quote",
          labour_hours: Number(labourHours) || 0,
          hourly_rate: Number(hourlyRate) || 0,
          gst_inclusive: gstInclusive,
          subtotal: totals.subtotal,
          gst: totals.gst,
          total: totals.total,
        }}
        labelKey="email.quote.label"
        descKey="email.quote.desc"
      />

      <HireUsCTA
        campaign="quote-calc"
        headline="Tired of quoting in spreadsheets?"
        subline="Our Growth plan ($75 + GST/mo) includes proper invoicing in Prosaic, plus your GST return filed with IRD — so you can quit copy-pasting cells."
      />
    </div>
  );
}

function Row({ label, value, big, accent, testid }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="label-mono">{label}</span>
      <span
        data-testid={testid}
        className={`${big ? "text-2xl text-white" : "text-base"} ${
          accent ? "text-[var(--primary)]" : "text-white"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

function LabeledNumber({ label, value, onChange, testid }) {
  return (
    <div>
      <div className="label-mono mb-2">{label}</div>
      <input
        type="number"
        step="0.01"
        className="input-sharp"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
      />
    </div>
  );
}
